import {
  CondicaoVisibilidade,
  FormularioVisivel
} from '../type/FormularioDinamico';

export interface ContextoAvaliacao {
  campos: Record<string, unknown>;
  respostas: Record<string, Record<string, unknown>>;
}

const normalizarTexto = (valor: unknown): string =>
  String(valor ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const comoNumero = (valor: unknown): number | undefined => {
  if (valor === null || valor === undefined || valor === '') {
    return undefined;
  }
  const numero = Number(valor);
  return Number.isNaN(numero) ? undefined : numero;
};

const comoData = (valor: unknown): number | undefined => {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(valor)) {
    return undefined;
  }
  const tempo = Date.parse(valor);
  return Number.isNaN(tempo) ? undefined : tempo;
};

const comparar = (esquerda: unknown, direita: unknown): number | undefined => {
  const numeroEsquerda = comoNumero(esquerda);
  const numeroDireita = comoNumero(direita);
  if (numeroEsquerda !== undefined && numeroDireita !== undefined) {
    return numeroEsquerda - numeroDireita;
  }

  const dataEsquerda = comoData(esquerda);
  const dataDireita = comoData(direita);
  if (dataEsquerda !== undefined && dataDireita !== undefined) {
    return dataEsquerda - dataDireita;
  }

  return normalizarTexto(esquerda).localeCompare(normalizarTexto(direita));
};

const comoLista = (valor?: string): unknown[] => {
  if (!valor) {
    return [];
  }
  try {
    const parsed = JSON.parse(valor);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return valor.split(',').map(item => item.trim());
  }
};

const contem = (valor: unknown, procurado?: string): boolean => {
  if (Array.isArray(valor)) {
    return valor.some(
      item => normalizarTexto(item) === normalizarTexto(procurado)
    );
  }
  return normalizarTexto(valor).includes(normalizarTexto(procurado));
};

const estaVazio = (valor: unknown): boolean =>
  valor === null ||
  valor === undefined ||
  valor === '' ||
  (Array.isArray(valor) && valor.length === 0);

export const resolverOperando = (
  condicao: CondicaoVisibilidade,
  contexto: ContextoAvaliacao
): unknown => {
  if (condicao.fonte === 'CONTEXTO') {
    return contexto.campos[condicao.campo];
  }

  const [painelFormId, controlName] = condicao.campo.split('::');
  return contexto.respostas[painelFormId]?.[controlName];
};

export const avaliarCondicao = (
  condicao: CondicaoVisibilidade,
  contexto: ContextoAvaliacao
): boolean => {
  const valor = resolverOperando(condicao, contexto);

  switch (condicao.operador) {
    case 'EQUAL_TO':
      return normalizarTexto(valor) === normalizarTexto(condicao.valor);
    case 'NOT_EQUAL_TO':
      return normalizarTexto(valor) !== normalizarTexto(condicao.valor);
    case 'IN':
      return comoLista(condicao.valor).some(
        item => normalizarTexto(item) === normalizarTexto(valor)
      );
    case 'NOT_IN':
      return !comoLista(condicao.valor).some(
        item => normalizarTexto(item) === normalizarTexto(valor)
      );
    case 'GREATER_THAN':
      return (comparar(valor, condicao.valor) ?? 0) > 0;
    case 'GREATER_OR_EQUAL':
      return (comparar(valor, condicao.valor) ?? 0) >= 0;
    case 'LESS_THAN':
      return (comparar(valor, condicao.valor) ?? 0) < 0;
    case 'LESS_OR_EQUAL':
      return (comparar(valor, condicao.valor) ?? 0) <= 0;
    case 'CONTAINS':
      return contem(valor, condicao.valor);
    case 'NOT_CONTAINS':
      return !contem(valor, condicao.valor);
    case 'IS_EMPTY':
      return estaVazio(valor);
    case 'IS_NOT_EMPTY':
      return !estaVazio(valor);
    default:
      return true;
  }
};

export const avaliarVisibilidade = (
  condicoes: CondicaoVisibilidade[],
  contexto: ContextoAvaliacao
): boolean => {
  if (!condicoes || condicoes.length === 0) {
    return true;
  }

  const ordenadas = [...condicoes].sort((a, b) => a.ordem - b.ordem);

  const satisfeito = ordenadas.reduce<boolean>(
    (acumulado, condicao, indice) => {
      const atual = avaliarCondicao(condicao, contexto);

      if (indice === 0) {
        return atual;
      }

      return condicao.operadorLogico === 'OU'
        ? acumulado || atual
        : acumulado && atual;
    },
    false
  );

  return ordenadas[0].efeito === 'OCULTAR' ? !satisfeito : satisfeito;
};

export const montarContexto = (
  campos: Record<string, unknown>,
  formularios: FormularioVisivel[]
): ContextoAvaliacao => ({
  campos,
  respostas: formularios.reduce<Record<string, Record<string, unknown>>>(
    (acumulado, formulario) => ({
      ...acumulado,
      [formulario.painelFormId]: formulario.values || {}
    }),
    {}
  )
});

export const filtrarVisiveis = <T extends FormularioVisivel>(
  formularios: T[],
  campos: Record<string, unknown>
): T[] => {
  const contexto = montarContexto(campos, formularios);

  return formularios.filter(formulario =>
    avaliarVisibilidade(formulario.visibilidade, contexto)
  );
};
